import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: {
          id: action.payload.id,
          username: action.payload.username,
          first_name: action.payload.first_name,
          last_name: action.payload.last_name,
          avatar: action.payload.avatar,
          role: action.payload.role,
        },
        isAuthenticated: true,
      };

    // New action to handle profile updates
    case "UPDATE_USER":
      return {
        ...state,
        user: {
          ...state.user, // Keep existing fields like id and role
          ...action.payload, // Overwrite with new data
        },
      };

    case "LOGOUT":
      return { user: null, isAuthenticated: false };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    isAuthenticated: false,
  });

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("selectedTheme");
    sessionStorage.removeItem("user_data"); // Also remove user_data on logout
    localStorage.removeItem("activeTab");
    dispatch({ type: "LOGOUT" });

    const themes = ["template1", "template2", "template3","template4","template5"];
    document.body.classList.remove(...themes.map((t) => `theme-${t}`));
    document.body.classList.add("theme-template2");

    if (window.location.pathname !== "/") {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    const token = sessionStorage.getItem("access_token");
    const userData = sessionStorage.getItem("user_data");

    if (token && userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        dispatch({
          type: "LOGIN_SUCCESS",
          payload: parsedUserData,
        });
      } catch (error) {
        console.error("Error parsing user data:", error);
        handleLogout();
      }
    }
    setLoading(false);
  }, [handleLogout]);

  // New function to update user data in context and sessionStorage
  const updateUser = useCallback((newUserData) => {
    try {
      // 1. Dispatch action to update the state in the context
      dispatch({ type: "UPDATE_USER", payload: newUserData });

      // 2. Update sessionStorage so the data persists on refresh
      const currentData = JSON.parse(sessionStorage.getItem("user_data")) || {};
      const updatedData = { ...currentData, ...newUserData };
      sessionStorage.setItem("user_data", JSON.stringify(updatedData));
    } catch (error) {
      console.error("Failed to update user data:", error);
    }
  }, []);

  const login = async (username, password) => {
    const apiBase = process.env.REACT_APP_API_URL_CODE_CONVERSION;

    try {
      const formData = new URLSearchParams();
      formData.append("username", username);
      formData.append("password", password);

      const response = await fetch(`${apiBase}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      sessionStorage.setItem("access_token", data.access_token);
      sessionStorage.setItem("user_data", JSON.stringify(data.user_data));
      sessionStorage.setItem("user_id", data.user_data.id);

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: data.user_data,
      });

      return data;
    } catch (error) {
      console.error("Login Failed:", error);
      return null;
    }
  };

  if (loading) {
    return <div></div>;
  }

  // Expose the new 'updateUser' function through the context's value
  return (
    <AuthContext.Provider
      value={{ state, login, logout: handleLogout, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};